/**
 * Response Sanitizer for Web Research answers.
 *
 * Normalizes LLM output so it renders cleanly inside chat panels:
 * - Removes accidental repeated headings
 * - Strips giant Markdown headings (### Title becomes Title)
 * - Collapses excessive blank lines
 * - Preserves bold, lists, links, code
 * - Escapes raw HTML that could break layout
 * - Handles empty / null / malformed input gracefully
 */

const MAX_HEADING_LEVEL = 3; // anything above h3 gets demoted

/**
 * Main entry point — returns sanitized markdown string.
 */
export function sanitizeWebAnswer(raw) {
  if (!raw || typeof raw !== 'string') return '';

  let text = raw;

  // 1. Strip common LLM prefixes that add noise
  text = text.replace(/^(here\s+(is|are)\s+(the\s+)?(answer|result|response|summary)[\s:.-]*)/i, '');
  text = text.replace(/^(based\s+on\s+the\s+(provided\s+)?(search\s+)?(results?|context|sources?)[\s,:.-]*)/i, '');
  text = text.replace(/^(according\s+to\s+the\s+(provided\s+)?(search\s+)?(results?|context|sources?)[\s,:.-]*)/i, '');

  // 2. Remove accidental repeated headings (same line or consecutive identical lines)
  const lines = text.split('\n');
  const deduped = [];
  let prevLine = '';
  for (const line of lines) {
    const trimmed = line.trim();
    // Skip exact duplicate consecutive lines
    if (trimmed && trimmed === prevLine) continue;
    // Skip lines that are just a heading repeated as body text
    if (/^#{1,6}\s/.test(trimmed) && deduped.length > 0) {
      const plainHeading = trimmed.replace(/^#{1,6}\s+/, '');
      const lastContent = deduped[deduped.length - 1].trim();
      if (lastContent === plainHeading) continue;
    }
    deduped.push(line);
    prevLine = trimmed;
  }
  text = deduped.join('\n');

  // 3. Demote headings above MAX_HEADING_LEVEL (### becomes ###)
  //    and strip any heading that is just the question repeated
  text = text.replace(/^(#{1,6})\s+(.+)$/gm, (match, hashes, content) => {
    const level = hashes.length;
    if (level > MAX_HEADING_LEVEL) {
      const demoted = '#'.repeat(MAX_HEADING_LEVEL);
      return `${demoted} ${content}`;
    }
    return match;
  });

  // 4. Remove headings that duplicate the user question (common LLM behavior)
  //    We detect this by checking if heading content is very long (likely a full sentence)
  text = text.replace(/^(#{1,6})\s+(.{80,})$/gm, (match, hashes, content) => {
    // If heading is very long, it's probably a sentence — convert to bold paragraph
    return `**${content.trim()}**`;
  });

  // 5. Collapse 3+ consecutive blank lines into 2
  text = text.replace(/\n{3,}/g, '\n\n');

  // 6. Trim leading/trailing whitespace
  text = text.trim();

  // 7. If nothing meaningful remains, return empty
  if (!text || text.replace(/[\s\n#*>-]/g, '').length < 5) return '';

  return text;
}

/**
 * Sanitize sources array — remove duplicates, ensure required fields.
 */
export function sanitizeSources(sources) {
  if (!Array.isArray(sources)) return [];

  const seen = new Set();
  const clean = [];

  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;

    const url = src.url || '';
    const title = (src.title || '').trim();
    const domain = (src.domain || '').trim();

    // Skip duplicates by URL
    if (url && seen.has(url)) continue;
    if (url) seen.add(url);

    // Ensure minimum viable source object
    clean.push({
      title: title || domain || 'Untitled Source',
      url: url,
      domain: domain || extractDomain(url),
      summary: (src.summary || '').substring(0, 200),
    });
  }

  return clean;
}

/**
 * Extract domain from URL for display.
 */
function extractDomain(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Check if a response is a genuine error (not just weak results).
 */
export function isGenuineError(text) {
  if (!text) return true;
  const lower = text.toLowerCase();
  const genuineErrors = [
    'web search service is temporarily unavailable',
    'required ai model not found',
    'cannot connect to ollama',
    'ollama not available',
    'no search results found',
    'search provider returned no results',
    'web search encountered an error',
  ];
  return genuineErrors.some((e) => lower.includes(e));
}
